import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { CaptchaComponent } from '../captcha/captcha.component';
import { environment } from '../../../environments/environment';
export interface SavedUser {
  profileImage: string;
  name: string;
  email: string;
  date?: string;
}

/**
 * Component for switching between saved user accounts and signing in quickly with stored credentials.
 * @component
 * @selector app-switch-account
 * @example
 *   <app-switch-account [savedUsers]="savedUsersList" (useAnotherAccount)="handleUseAnother()"></app-switch-account>
 * @input SavedUser[] savedUsers - Array of previously saved user accounts displayed for quick selection.
 * @output EventEmitter<void> useAnotherAccount - Emitted when the user chooses to use another account (triggers parent to show full login/registration).
 *
 * Description:
 * - Displays a list of saved users, allows selecting a user, entering a password, and performing a quick sign-in.
 * - Manages UI state including selectedUser, isSubmitting, passwordError, showPassword, and showCaptcha.
 * - Integrates with a child CaptchaComponent (via @ViewChild) to verify user input and regenerate captchas on failure.
 * - Uses a reactive FormGroup (loginForm) with password validators (required, minLength).
 *
 * Key methods:
 * - selectUser(user: SavedUser): Selects a saved user, resets form and error/captcha state.
 * - onUseAnotherAccount(): Emits the useAnotherAccount event for parent-level handling.
 * - togglePasswordVisibility(): Toggles the showPassword flag to reveal or hide the password input.
 * - login(): Validates the selected user, optional captcha, and form; calls AuthService.login; handles success, error messages, isSubmitting state, and captcha regeneration on repeated failures.
 * - goBack(): Clears the selectedUser to return to the saved users list.
 *
 * Dependencies:
 * - Injects Router and AuthService via constructor.
 * - References environment.imageUrl for avatar/resource resolution.
 *
 * Lifecycle hooks:
 * - No explicit Angular lifecycle hooks implemented in this class; component initializes its reactive form and local state on creation. Captcha interactions rely on the child component reference which becomes available after child initialization.
 */
@Component({
  selector: 'app-switch-account',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, CaptchaComponent],
  templateUrl: './switch-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './switch-account.component.scss',
})
export class SwitchAccountComponent {
  @Input() savedUsers: SavedUser[] = [];
  @Output() useAnotherAccount = new EventEmitter<void>();
  @ViewChild(CaptchaComponent) captchaComponent!: CaptchaComponent;
  imageUrl = environment.imageUrl;
  selectedUser: SavedUser | null = null;
  isSubmitting = false;
  passwordError = '';
  failedImageEmails = new Set<string>();
  selectedUserImageFailed = false;

  getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  onImageError(email: string): void {
    this.failedImageEmails.add(email);
  }

  private isDefaultPlaceholder(url: string): boolean {
    return url.includes('Frame%201171276019') || url.includes('Frame 1171276019');
  }

  showInitials(user: SavedUser): boolean {
    return (
      !user.profileImage ||
      this.isDefaultPlaceholder(user.profileImage) ||
      this.failedImageEmails.has(user.email)
    );
  }

  loginForm = new FormGroup({
    password: new FormControl('', [Validators.required, Validators.minLength(2)]),
  });

  showPassword = false;
  showCaptcha = false;

  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  selectUser(user: SavedUser) {
    this.selectedUser = user;
    this.passwordError = '';
    this.showCaptcha = false;
    this.selectedUserImageFailed = false;
    this.loginForm.reset();
  }

  onUseAnotherAccount() {
    this.useAnotherAccount.emit();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  /**
   * Attempts to log in the currently selected user: validates captcha (if enabled), checks form validity,
   * sets submitting state, sends credentials to authService, and handles success or error responses.
   * @example
   * ogin()
   * undefined
   * @returns {void} Performs login side effects and does not return a value.
   */
  login() {
    if (!this.selectedUser) {
      return;
    }

    if (this.showCaptcha) {
      const isValid = this.captchaComponent.verifyCaptcha(
        this.captchaComponent.captchaInput.nativeElement.value
      );
      if (!isValid) {
        return;
      }
    }

    if (this.loginForm.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.passwordError = '';

    const credentials = {
      email: this.selectedUser.email,
      password: this.loginForm.value.password,
    };

    this.authService.login(credentials as any).subscribe({
      next: () => {
        this.isSubmitting = false;
      },
      error: err => {
        this.isSubmitting = false;
        if (err && err.message) {
          if (err.message.includes('Incorrect password')) {
            this.passwordError = 'Invalid credentials';
          } else {
            this.passwordError = 'Invalid credentials';
          }

          if (this.showCaptcha) {
            this.captchaComponent.generateCaptcha();
          }
          this.showCaptcha = true;
        } else {
          this.passwordError = 'Invalid credentials';
        }
        console.error(err);
      },
    });
  }

  goBack() {
    this.selectedUser = null;
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
